import { Component, OnInit, signal, inject } from '@angular/core';
import { RouterOutlet } from '@angular/router';
import { finalize, forkJoin, of, switchMap } from 'rxjs';

import { ThemeService } from '../../services/theme.service';
import { AuthService } from '../../auth/auth.service';
import { LoaderService } from '../../services/loader.service';
import { NavigationService } from '../../services/navigation.service';
import { PermissionsService } from '../../services/permissions.service';
import { MeService } from '../../services/me.service';
import { TenantService } from '../../services/tenant.service';
import { AppSidebarComponent } from '../app-sidebar/app-sidebar.component';
import { UserAvatarComponent } from '../user-avatar/user-avatar.component';
import { IngestionProgressComponent } from '@shared/components/ingestion-progress/ingestion-progress.component';

@Component({
  selector: 'app-layout',
  imports: [RouterOutlet, AppSidebarComponent, UserAvatarComponent, IngestionProgressComponent],
  templateUrl: './app-layout.component.html'
})
export class AppLayoutComponent implements OnInit {
  readonly themeService = inject(ThemeService);
  readonly authService = inject(AuthService);
  readonly loaderService = inject(LoaderService);
  private readonly navigationService = inject(NavigationService);
  private readonly permissionsService = inject(PermissionsService);
  private readonly meService = inject(MeService);
  private readonly tenantService = inject(TenantService);

  sidebarOpen = signal(false);
  sidebarCollapsed = signal(false);
  accessLoading = signal(false);
  accessReady = signal(false);
  accessIssue = signal<'session' | 'no-tenants' | 'access' | null>(null);

  ngOnInit() {
    this.loadAccessContext();
  }

  retryAccessContext() {
    this.meService.clear();
    this.permissionsService.clear();
    this.navigationService.clear();
    this.loadAccessContext();
  }

  private loadAccessContext() {
    if (this.accessLoading()) {
      return;
    }

    this.accessLoading.set(true);
    this.accessReady.set(false);
    this.accessIssue.set(null);

    // 1. Load identity first so we can resolve the tenant key before fetching
    //    navigation and permissions (both require X-Tenant-Key to return data).
    this.meService.load().pipe(
      switchMap(me => {
        if (!me) {
          this.accessIssue.set('session');
          return of(null);
        }

        if (me.tenants.length === 0) {
          this.tenantService.ensureValidTenant([], null);
          this.accessIssue.set('no-tenants');
          return of(null);
        }

        // A tenant stored by a previous user/session must never leak into this login.
        this.tenantService.ensureValidTenant(
          me?.tenants.map(tenant => tenant.tenantKey) ?? [],
          me?.defaultTenantKey
        );

        // 2. Now that the tenant is known, load both access projections together.
        return forkJoin({
          permissions: this.permissionsService.load(),
          navigation: this.navigationService.load()
        });
      }),
      finalize(() => this.accessLoading.set(false))
    ).subscribe(result => {
      if (result && (this.permissionsService.loadError() || this.navigationService.loadError())) {
        this.accessIssue.set('access');
        return;
      }

      this.accessReady.set(!!result);
    });
  }

  toggleSidebar() {
    this.sidebarOpen.update(v => !v);
  }

  toggleCollapsed() {
    this.sidebarCollapsed.update(v => !v);
  }

  handleLogout() {
    this.authService.logout();
  }

}
